import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState } from "react";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { DownloadSimple } from "@phosphor-icons/react";
import NudgeManagementTable from "../../components/adminDashboard/NudgeManagementTable";
import {
  useGetAllStudentNudgePreferencesQuery,
  useExportStudentNudgePreferencesLazyQuery,
  StudentNudgePreferenceDetails,
} from "../../graphql/generated/graphql";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import ErrorLoader from "../../components/common/status/ErrorLoader/ErrorLoader";

const SORT_ITEMS = [{ label: "Name (A-Z)", value: "name_asc" }];

function NudgeManagementView() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Student Preferences");

  const { data, loading, error } = useGetAllStudentNudgePreferencesQuery({
    fetchPolicy: "network-only",
  });

  const [exportData, { loading: exporting }] = useExportStudentNudgePreferencesLazyQuery({
    fetchPolicy: "network-only",
  });

  const handleExport = async () => {
    try {
      const res = await exportData();
      if (res.data?.exportStudentNudgePreferences?.success && res.data.exportStudentNudgePreferences.data) {
        const csvString = res.data.exportStudentNudgePreferences.data;
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", `nudge_management_export_${Date.now()}.csv`);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const preferencesResponse = data?.getAllStudentNudgePreferences;
  const preferencesErrorMessage =
    error?.message ??
    (preferencesResponse && !preferencesResponse.success
      ? preferencesResponse.message || "Failed to fetch student nudge preferences."
      : null);
  const allPreferences = preferencesResponse?.success ? (preferencesResponse.data ?? []) : [];

  const filteredPreferences = allPreferences.filter((pref) => {
    if (!searchQuery) return true;
    const nameMatch = pref.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const emailMatch = pref.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return nameMatch || emailMatch;
  }) as StudentNudgePreferenceDetails[];

  const tabs = ["Nudge Activity", "Funnel Analytics", "Student Preferences"];

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.headerContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ type: "timing", duration: 500 }}>
            <CustomText
              text="Nudge Management"
              size="l"
              useTranslationText={false}
              textType="display"
              weight="ultraBold"
              style={{ color: "#101828" }}
            />
            <CustomSpacing type="vertical" size="s" />
            <CustomText
              text="Track nudges and response rates for your students."
              size="s"
              weight="medium"
              useTranslationText={false}
              style={{ color: "#667085" }}
            />
            <CustomSpacing type="vertical" size="l" />

            <View style={styles.tabContainer}>
              {tabs.map((tab) => (
                <CustomButton
                  key={tab}
                  text={tab}
                  onPress={() => setActiveTab(tab)}
                  styleType="transparent"
                  style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
                  textStyle={[styles.tabButtonText, activeTab === tab && styles.activeTabButtonText]}
                  useTranslationText={false}
                />
              ))}
            </View>
          </MotiView>
        </View>

        <View style={styles.contentContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 20 }}
            animate={{ opacity: 1, translateY: 0 }}
            delay={10}
            style={{ flex: 1, width: "100%", zIndex: 1 }}
            // @ts-expect-error - moti transition prop type mismatch
            transition={{ type: "timing", duration: 500 }}>
            <AdminTable
              style={{
                flex: 1,
                zIndex: 1,
                borderRadius: 12,
                overflow: "hidden",
                borderWidth: 1,
                borderColor: "#EAECF0",
              }}>
              <AdminTableHeader
                variant="white"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                placeholder="Search student..."
                rightContent={
                  <CustomButton
                    text="Export"
                    size="s"
                    styleType="transparent"
                    leftIcon={<DownloadSimple size={16} color="#6B7280" />}
                    onPress={handleExport}
                    useTranslationText={false}
                    loading={exporting}
                    textStyle={{ fontSize: 13, color: "#475569" }}
                  />
                }
              />

              {activeTab === "Student Preferences" ? (
                loading ? (
                  <View style={styles.loaderContainer}>
                    <ActivityIndicator size="large" color="#0084FF" />
                  </View>
                ) : preferencesErrorMessage ? (
                  <View style={styles.loaderContainer}>
                    <ErrorLoader message={preferencesErrorMessage} />
                  </View>
                ) : (
                  <NudgeManagementTable preferences={filteredPreferences} />
                )
              ) : (
                <View style={styles.loaderContainer}>
                  <CustomText
                    text={`${activeTab} dashboard is coming soon.`}
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#667085" }}
                  />
                </View>
              )}
            </AdminTable>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F9FAFB",
    // @ts-expect-error - works on web
    minHeight: "100vh",
  },
  headerContainer: {
    paddingHorizontal: 32,
    paddingTop: 40,
    backgroundColor: "#F9FAFB",
  },
  contentContainer: {
    padding: 32,
    flex: 1,
  },
  tabContainer: {
    flexDirection: "row",
    gap: 8,
    backgroundColor: "#fff",
    padding: 4,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    alignSelf: "flex-start",
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  activeTabButton: {
    backgroundColor: "#e6ebff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabButtonText: {
    fontSize: 14,
    color: "#667085",
    fontWeight: "400",
  },
  activeTabButtonText: {
    color: "#012298",
    fontWeight: "400",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    backgroundColor: "#fff",
  },
});

export default NudgeManagementView;
